import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, Plus, Star, Trash2 } from "lucide-react";
import { Button } from "@finora/ui";
import { api } from "../../lib/api";

export function BankAccountsSection() {
  const queryClient = useQueryClient();
  const [accountNumber, setAccountNumber] = useState("");
  const [bankName, setBankName] = useState("");

  const { data: accounts, isLoading } = useQuery({
    queryKey: ["company-bank-accounts"],
    queryFn: () => api.listCompanyBankAccounts(),
  });

  const { data: companySettings } = useQuery({
    queryKey: ["company-settings"],
    queryFn: () => api.getCompanySettings(),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["company-bank-accounts"] });

  const create = useMutation({
    mutationFn: () => api.createCompanyBankAccount({ accountNumber, bankName: bankName || null }),
    onSuccess: () => {
      invalidate();
      setAccountNumber("");
      setBankName("");
    },
  });

  const setDefault = useMutation({
    mutationFn: (id: string) => api.setDefaultCompanyBankAccount(id),
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.deleteCompanyBankAccount(id),
    onSuccess: invalidate,
  });

  // Jedna zajednicka adresa za uvoz izvoda mejlom - PO FIRMI (PIB), ne po
  // racunu (04.10.2026, ispravljena greska: ranije se ovde generisala
  // adresa u "stari" obliku <PIB>_<broj racuna>@import.hafiskal.rs, koji NIJE
  // ta koja stvarno radi - mejl servis prepoznaje samo
  // "obradaizvoda_<PIB>@import.hafiskal.rs" i sam banku/racun prepoznaje iz
  // sadrzaja izvoda, vidi claude/obrada-izvoda-minimax-odrediste.md).
  const importEmail = companySettings?.taxId ? `obradaizvoda_${companySettings.taxId}@import.hafiskal.rs` : null;

  return (
    <div className="mt-5 border-t border-border pt-4">
      <h2 className="mb-3 text-sm font-semibold text-slate-800">Bankovni računi</h2>
      <p className="mb-3 text-xs text-slate-400">
        Podrazumevani račun (zvezdica) se koristi za IPS QR kod na fakturama i eFakture. Firma može
        imati više računa (npr. za uvoz izvoda iz više banaka preko API-ja) - svi moraju biti upisani
        ovde da bi se izvod sa tog računa prepoznao i uvezao.
      </p>

      {importEmail ? (
        <div className="mb-3 flex items-center gap-1.5 rounded-lg bg-app-bg px-3 py-2 text-xs text-slate-600">
          <span>
            Email za uvoz izvoda (važi za SVE račune firme):{" "}
            <span className="font-mono font-medium text-slate-800">{importEmail}</span>
          </span>
          <button
            onClick={() => navigator.clipboard.writeText(importEmail).catch(() => undefined)}
            className="text-slate-400 hover:text-brand"
            title="Kopiraj"
          >
            <Copy className="h-3.5 w-3.5" aria-hidden />
          </button>
        </div>
      ) : (
        <p className="mb-3 text-xs text-amber-600">
          Unesi PIB firme (tab "Firma" iznad) da bi se generisala email adresa za uvoz.
        </p>
      )}

      {isLoading ? (
        <p className="text-sm text-slate-400">Učitavanje...</p>
      ) : !accounts || accounts.length === 0 ? (
        <p className="text-sm text-slate-400">Još nema dodatih računa.</p>
      ) : (
        <div className="mb-3 space-y-1.5">
          {accounts.map((acc) => (
            <div key={acc.id} className="flex items-center justify-between rounded-lg bg-app-bg px-3 py-2 text-sm">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => !acc.isDefault && setDefault.mutate(acc.id)}
                  title={acc.isDefault ? "Podrazumevani račun" : "Postavi kao podrazumevani"}
                  className={acc.isDefault ? "text-amber-500" : "text-slate-300 hover:text-amber-400"}
                >
                  <Star className="h-4 w-4" fill={acc.isDefault ? "currentColor" : "none"} aria-hidden />
                </button>
                <span className="font-medium text-slate-800">{acc.accountNumber}</span>
                {acc.bankName && <span className="text-xs text-slate-400">· {acc.bankName}</span>}
              </div>
              <button
                onClick={() => {
                  if (window.confirm("Obrisati ovaj račun?")) remove.mutate(acc.id);
                }}
                className="text-slate-400 hover:text-red-600"
              >
                <Trash2 className="h-3.5 w-3.5" aria-hidden />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-end gap-2">
        <div className="flex-1">
          <label className="mb-1 block text-xs font-medium text-slate-500">Broj računa</label>
          <input
            value={accountNumber}
            onChange={(e) => setAccountNumber(e.target.value)}
            placeholder="160-1234567890123-45"
            className="h-9 w-full rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
          />
        </div>
        <div className="flex-1">
          <label className="mb-1 block text-xs font-medium text-slate-500">Banka (opciono)</label>
          <input
            value={bankName}
            onChange={(e) => setBankName(e.target.value)}
            placeholder="npr. Banca Intesa"
            className="h-9 w-full rounded-lg border border-border px-2 text-sm focus:border-brand focus:outline-none"
          />
        </div>
        <Button size="sm" disabled={!accountNumber || create.isPending} onClick={() => create.mutate()}>
          <Plus className="h-3.5 w-3.5" aria-hidden /> Dodaj
        </Button>
      </div>
    </div>
  );
}
